/**
 * appleSignIn.js
 *
 * Thin wrapper around Apple's "Sign in with Apple JS" SDK for the web.
 *
 * Security note: this module only obtains Apple's signed identity token in the
 * browser. It never decodes or trusts the token. The token is posted to
 * `/auth/apple`, where the backend verifies the signature, issuer and audience
 * against Apple's JWKS (see apps/backend/src/services/appleIdentityTokenVerifier.js).
 */

const APPLE_SDK_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const APPLE_SDK_SCRIPT_ID = 'aluvo-apple-auth-sdk';

let sdkPromise = null;
let initialized = false;

export function getAppleWebClientId() {
    return process.env.REACT_APP_APPLE_CLIENT_ID || '';
}

export function isAppleSignInConfigured() {
    return !!getAppleWebClientId();
}

function getRedirectUri() {
    if (process.env.REACT_APP_APPLE_REDIRECT_URI) return process.env.REACT_APP_APPLE_REDIRECT_URI;
    if (typeof window !== 'undefined') return `${window.location.origin}/login`;
    return '';
}

export function loadAppleSdk() {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return Promise.reject(new Error('Apple Sign-In is only available in the browser.'));
    }
    if (window.AppleID?.auth) return Promise.resolve(window.AppleID);
    if (sdkPromise) return sdkPromise;

    sdkPromise = new Promise((resolve, reject) => {
        const existing = document.getElementById(APPLE_SDK_SCRIPT_ID);
        const onLoad = () => (window.AppleID?.auth ? resolve(window.AppleID) : reject(new Error('Apple Sign-In failed to load.')));
        const onError = () => {
            sdkPromise = null;
            reject(new Error('Apple Sign-In failed to load.'));
        };
        if (existing) {
            existing.addEventListener('load', onLoad, { once: true });
            existing.addEventListener('error', onError, { once: true });
            return;
        }
        const script = document.createElement('script');
        script.id = APPLE_SDK_SCRIPT_ID;
        script.src = APPLE_SDK_SRC;
        script.async = true;
        script.defer = true;
        script.onload = onLoad;
        script.onerror = onError;
        document.head.appendChild(script);
    });
    return sdkPromise;
}

async function ensureInitialized() {
    const AppleID = await loadAppleSdk();
    if (!initialized) {
        AppleID.auth.init({
            clientId: getAppleWebClientId(),
            scope: 'name email',
            redirectURI: getRedirectUri(),
            usePopup: true,
        });
        initialized = true;
    }
    return AppleID;
}

/**
 * Opens the Apple sign-in popup and resolves with the raw identity token plus
 * the (first-sign-in only) name/email Apple shares. Rejects on cancel/failure.
 */
export async function signInWithApple() {
    if (!isAppleSignInConfigured()) throw new Error('Apple Sign-In is not configured.');
    const AppleID = await ensureInitialized();
    const response = await AppleID.auth.signIn();
    const identityToken = response?.authorization?.id_token;
    if (!identityToken) throw new Error('No identity token returned from Apple.');
    const user = response?.user || null;
    return {
        identityToken,
        email: user?.email || undefined,
        fullName: user?.name
            ? { givenName: user.name.firstName || '', familyName: user.name.lastName || '' }
            : undefined,
    };
}

export function isAppleSignInCancelled(error) {
    const code = error?.error || error?.code || '';
    return /popup_closed_by_user|user_cancelled_authorize|user_trigger_new_signin_flow/i.test(String(code));
}

export function __resetAppleSignInForTests() {
    sdkPromise = null;
    initialized = false;
}
